import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

/* ── MUI imports ──────────────────────────────────────────────── */
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import CloseIcon from "@mui/icons-material/Close";
import BoltIcon from "@mui/icons-material/Bolt";
import EditIcon from "@mui/icons-material/Edit";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import Langs from "../../app/lang/langs";
import ProgressViewCollab from "./ProgressViewCollab";
import { getProjectAptitudesWithValues, answerAptitudeDailyBase, setCollabProjectFormVisibility, setOnAnswerProject, setProjectAptitudes } from "./vcompSlice";
import { showErrorToast } from "../../app/utils/helpers";
import styles from './VcompCompnents.module.css';

/* ── Constants ────────────────────────────────────────────────── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255, 144, 21, 0.08)";
const ORANGE_GLOW = "rgba(255, 144, 21, 0.25)";

const PROGRESS_STEPS_PRACTICAL = [
    { label: "Ne sait pas encore ce que c'est", value: "0" },
    { label: "Expliqué", value: "1" },
    { label: "Vu (Double passif)", value: "2" },
    { label: "Réalisé avec accompagnement (Double Actif)", value: "3" },
    { label: "Réalisé sans aide (Double Actif)", value: "4" },
    { label: "Maîtrisé (Autonomie)", value: "5" }
];

const PROGRESS_STEPS_THEORETICAL = [
    { label: "Rien fait", value: "0" },
    { label: "Lu/Expliqué une fois (en partie)", value: "1" },
    { label: "Lu/Expliqué une fois (totalité)", value: "2" },
    { label: "Compris/Connu en partie", value: "3" },
    { label: "Compris/Connu en totalité", value: "4" },
    { label: "Maîtrisé (Autonome)", value: "5" }
];

/* ── Transition ───────────────────────────────────────────────── */
const SlideTransition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Color helper for dot indicator ───────────────────────────── */
const getProgressColor = (num) => {
    if (num < 0 || num > 100) return '#9ca3af';
    if (num < 25) return '#ef4444';
    if (num < 50) return ORANGE;
    if (num < 75) return '#eab308';
    return '#22c55e';
};

/* ── ProgressSteps sub-component ──────────────────────────────── */
const ProgressSteps = ({ steps, selectedOption, setSelectedOption, currentProgress }) => (
    <Box sx={{ py: 0.5 }}>
        {steps.map((step, idx) => {
            const isSelected = selectedOption === step.value;
            const isDisabled = currentProgress > parseInt(step.value);
            const isCompleted = currentProgress >= parseInt(step.value) && parseInt(step.value) > 0;

            return (
                <Box
                    key={step.value}
                    onClick={() => !isDisabled && setSelectedOption(step.value)}
                    className={`${styles.collabProgressStep} ${isSelected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
                    sx={{
                        animation: `cpfFadeIn 300ms cubic-bezier(0, 0, 0.2, 1) ${idx * 50}ms both`,
                        '@keyframes cpfFadeIn': {
                            from: { opacity: 0, transform: 'translateY(8px)' },
                            to: { opacity: 1, transform: 'translateY(0)' },
                        },
                    }}
                >
                    {/* Step number badge */}
                    <Box
                        className={`${styles.collabStepNumber} ${
                            isSelected ? styles.collabStepNumberSelected :
                            isCompleted ? styles.collabStepNumberCompleted :
                            styles.collabStepNumberDefault
                        }`}
                    >
                        {isCompleted && !isSelected ? (
                            <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                        ) : (
                            step.value
                        )}
                    </Box>

                    {/* Custom checkbox */}
                    <Checkbox
                        checked={isSelected}
                        disabled={isDisabled}
                        onChange={() => setSelectedOption(step.value)}
                        sx={{
                            color: 'rgba(0, 0, 0, 0.15)',
                            p: 0,
                            '&.Mui-checked': {
                                color: ORANGE,
                            },
                            '&.Mui-disabled': {
                                color: 'rgba(0, 0, 0, 0.08)',
                            },
                            transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                        icon={<RadioButtonUncheckedIcon sx={{ fontSize: 22 }} />}
                        checkedIcon={<CheckCircleIcon sx={{ fontSize: 22 }} />}
                    />

                    {/* Label */}
                    <Typography className={styles.collabStepLabel}>
                        {step.label}
                    </Typography>
                </Box>
            );
        })}
    </Box>
);

/* ── Breadcrumb sub-component ─────────────────────────────────── */
const Breadcrumb = ({ category, subcategory }) => (
    <Box className={styles.collabBreadcrumb}>
        {category && (
            <Chip
                label={category}
                size="small"
                className={styles.collabBreadcrumbChip}
            />
        )}
        {category && subcategory && (
            <ChevronRightIcon sx={{ fontSize: 16, color: 'rgba(0, 0, 0, 0.18)' }} />
        )}
        {subcategory && (
            <Chip
                label={subcategory}
                size="small"
                className={styles.collabBreadcrumbChip}
            />
        )}
    </Box>
);

/* ── Aptitude Tab Header ──────────────────────────────────────── */
const AptitudeTabLabel = ({ apt }) => {
    const progressPct = apt.progress ? apt.progress * 20 : 0;
    const dotColor = getProgressColor(progressPct);

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Typography sx={{
                fontSize: '0.82rem',
                fontWeight: 500,
                letterSpacing: '-0.01em',
                whiteSpace: 'nowrap',
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }}>
                {apt.nom}
            </Typography>
            {apt.progress !== null && apt.progress !== undefined && (
                <Box sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: dotColor,
                    flexShrink: 0,
                    boxShadow: `0 0 6px ${dotColor}40`,
                    transition: 'all 300ms ease',
                }} />
            )}
        </Box>
    );
};

/* ── Main Component ───────────────────────────────────────────── */
const CollabProjectForm = () => {
    const dispatch = useDispatch();
    const visible = useSelector((state) => state.vcomp.collabProjectFormVisibility);
    const projectAptitudes = useSelector((state) => state.vcomp.projectAptitudesWithValues);
    const onAnswerProject = useSelector((state) => state.vcomp.onAnswerProject);
    const isMobile = useSelector((state) => state.settings.isMobile);

    const [activeIndex, setActiveIndex] = useState(0);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [localApt, setLocalApt] = useState([]);
    const [selectedOption, setSelectedOption] = useState('');

    useEffect(() => {
        if (onAnswerProject) {
            dispatch(getProjectAptitudesWithValues({ id_project: onAnswerProject.id }));
        }
    }, [onAnswerProject, dispatch]);

    useEffect(() => {
        if (projectAptitudes && projectAptitudes.categories) {
            setLocalApt(flattenData(projectAptitudes.categories));
        } else {
            setLocalApt([]);
        }
    }, [projectAptitudes]);

    useEffect(() => {
        if (localApt[activeIndex]) {
            setSelectedOption(localApt[activeIndex].progress !== null ? localApt[activeIndex].progress.toString() : '');
        }
    }, [activeIndex, localApt]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if ((event.key === 'Enter' || event.key === 'ArrowRight') && !!selectedOption) {
                handleNext();
            }
            if (event.key === 'ArrowLeft') {
                setActiveIndex((prevIndex) => {
                    if (prevIndex > 0) {
                        return prevIndex - 1;
                    }
                    return prevIndex;
                });
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [selectedOption]);

    const flattenData = (data) => {
        let aptitudes = [];
        const processAptitude = (aptitude, categoryId = null, categoryName = null, subcategoryId = null, subcategoryName = null) => {
            aptitudes.push({
                id: aptitude.id,
                nom: aptitude.nom,
                coef: aptitude.coef,
                visibility: aptitude.visibility,
                description: aptitude.description,
                progress: aptitude.progress,
                type: aptitude.type,
                pos: aptitude.pos,
                category_id: categoryId,
                category_name: categoryName,
                subcategory_id: subcategoryId,
                subcategory_name: subcategoryName,
                aspect: aptitude.aspect
            });
        };
        data.forEach(category => {
            if (category.scope === "category") {
                const categoryName = category.nom;
                const categoryId = category.id;
                if (category.children && category.children.length) {
                    category.children.forEach(item => {
                        if (item.scope === "subcategory") {
                            const subcategoryName = item.nom;
                            const subcategoryId = item.id;
                            item.children.forEach(aptitude => {
                                if (aptitude.scope === "aptitude") {
                                    processAptitude(aptitude, categoryId, categoryName, subcategoryId, subcategoryName);
                                }
                            });
                        } else if (item.scope === "aptitude") {
                            processAptitude(item, categoryId, categoryName);
                        }
                    });
                }
            } else if (category.scope === "aptitude") {
                processAptitude(category);
            }
        });
        return aptitudes;
    };

    const handleClose = () => {
        dispatch(setCollabProjectFormVisibility(false));
        dispatch(setOnAnswerProject(null));
        dispatch(setProjectAptitudes([]));
        setActiveIndex(0);
        setActiveTabIndex(0);
    };

    const handleNext = () => {
        if (localApt[activeIndex]) {
            let nextIndex = activeIndex;
            do {
                nextIndex++;
            } while (nextIndex < localApt.length && localApt[nextIndex].progress == "5");

            if (nextIndex < localApt.length && localApt[nextIndex]) {
                const currentApt = localApt[activeIndex];
                if (currentApt.progress !== selectedOption) {
                    try {
                        updateLocalData(activeIndex, selectedOption);
                        dispatch(answerAptitudeDailyBase({ id_aptitude: currentApt.id, progress: selectedOption }));
                        setSelectedOption('');
                    } catch (error) {
                        showErrorToast();
                        return;
                    }
                }
                setActiveIndex(nextIndex);
            } else {
                const currentApt = localApt[activeIndex];
                if (currentApt.progress !== selectedOption) {
                    try {
                        updateLocalData(activeIndex, selectedOption);
                        dispatch(answerAptitudeDailyBase({ id_aptitude: currentApt.id, progress: selectedOption }));
                        setSelectedOption('');
                    } catch (error) {
                        showErrorToast();
                        return;
                    }
                }
                handleClose();
            }
        }
    };

    const updateLocalData = (index, progress) => {
        localApt[index] = {
            ...localApt[index],
            progress: progress
        };
    };

    /* ── Body renderer ────────────────────────────────────────── */
    const body = () => {
        if (activeTabIndex === 0) {
            return <ProgressViewCollab nodes={projectAptitudes?.categories || []} />;
        }

        const currentApt = localApt[activeIndex] || {};
        const currentProgress = parseInt(currentApt.progress || '0', 10);

        if (localApt.length === 0) {
            return (
                <Box className={styles.collabEmptyState}>
                    <Box className={styles.collabEmptyIcon}>
                        <AssignmentOutlinedIcon sx={{ fontSize: 30, color: ORANGE, opacity: 0.7 }} />
                    </Box>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 600, color: '#6b7280' }}>
                        <Langs str="NO_APTITUDES_MESSAGE" />
                    </Typography>
                </Box>
            );
        }

        return (
            <Box sx={{ height: 'calc(100vh - 130px)', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                {/* Aptitude tabs - scrollable horizontal */}
                <Box className={styles.collabAptTabsWrap}>
                    <Tabs
                        value={activeIndex}
                        onChange={(e, val) => setActiveIndex(val)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            minHeight: 44,
                            px: 1,
                            '& .MuiTabs-indicator': {
                                backgroundColor: ORANGE,
                                height: 2.5,
                                borderRadius: '2px 2px 0 0',
                            },
                            '& .MuiTab-root': {
                                minHeight: 44,
                                textTransform: 'none',
                                fontSize: '0.82rem',
                                fontWeight: 500,
                                color: '#6b7280',
                                letterSpacing: '-0.01em',
                                py: 1,
                                px: 1.5,
                                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&.Mui-selected': {
                                    color: ORANGE,
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    color: '#374151',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                            },
                            '& .MuiTabs-scrollButtons': {
                                color: '#9ca3af',
                                '&:hover': { color: ORANGE },
                            },
                        }}
                    >
                        {localApt.map((tab) => (
                            <Tab key={tab.id} label={<AptitudeTabLabel apt={tab} />} />
                        ))}
                    </Tabs>
                </Box>

                {/* Scrollable content area */}
                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    pb: isMobile === 1 ? '100px' : '80px',
                    '&::-webkit-scrollbar': { width: '6px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255, 144, 21, 0.15)',
                        borderRadius: '4px',
                    },
                }}>
                    {/* Breadcrumb */}
                    <Breadcrumb
                        category={currentApt.category_name}
                        subcategory={currentApt.subcategory_name}
                    />

                    {/* Aptitude title & description */}
                    <Box className={styles.collabEntryCard}>
                        <Typography className={styles.collabAptTitle}>
                            {currentApt.nom}
                        </Typography>
                        {currentApt.description && (
                            <Typography className={styles.collabAptDescription}>
                                {currentApt.description}
                            </Typography>
                        )}

                        {/* Progress steps */}
                        {currentApt.aspect === "theoretical" ? (
                            <ProgressSteps
                                steps={PROGRESS_STEPS_THEORETICAL}
                                selectedOption={selectedOption}
                                setSelectedOption={setSelectedOption}
                                currentProgress={currentProgress}
                            />
                        ) : (
                            <ProgressSteps
                                steps={PROGRESS_STEPS_PRACTICAL}
                                selectedOption={selectedOption}
                                setSelectedOption={setSelectedOption}
                                currentProgress={currentProgress}
                            />
                        )}
                    </Box>
                </Box>

                {/* Keyboard hint (desktop only) */}
                {isMobile === 1 && (
                    <Box className={styles.collabKeyboardHint}>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                            <KeyboardIcon sx={{ fontSize: 13, opacity: 0.5 }} />
                            <Langs str="Vous pouvez utiliser les flèches de votre clavier pour naviguer plus rapidement" />
                        </Box>
                    </Box>
                )}

                {/* Bottom navigation */}
                <Box className={styles.collabBottomBar}>
                    <Button
                        variant="outlined"
                        disabled={activeIndex <= 0}
                        onClick={() => setActiveIndex(prevIndex => prevIndex - 1)}
                        startIcon={<NavigateBeforeIcon />}
                        sx={{
                            borderColor: 'rgba(0, 0, 0, 0.12)',
                            color: '#374151',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 550,
                            fontSize: '0.85rem',
                            px: 2.5,
                            py: 1,
                            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                borderColor: ORANGE,
                                color: ORANGE,
                                backgroundColor: ORANGE_LIGHT,
                                transform: 'translateX(-2px)',
                            },
                            '&:active': {
                                transform: 'scale(0.97)',
                            },
                            '&.Mui-disabled': {
                                borderColor: 'rgba(0, 0, 0, 0.06)',
                                color: 'rgba(0, 0, 0, 0.20)',
                            },
                        }}
                    >
                        <Langs str="Previous" />
                    </Button>

                    {/* Counter */}
                    <Box className={styles.collabCounterBadge}>
                        <span className={styles.collabCounterBadgeCurrent}>{activeIndex + 1}</span>
                        <span>/</span>
                        <span>{localApt.length}</span>
                    </Box>

                    <Button
                        variant="contained"
                        disabled={!selectedOption}
                        onClick={handleNext}
                        endIcon={<NavigateNextIcon />}
                        sx={{
                            background: `linear-gradient(135deg, ${ORANGE}, #FFC100)`,
                            color: '#fff',
                            borderRadius: '12px',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            px: 2.5,
                            py: 1,
                            boxShadow: `0 4px 14px ${ORANGE_GLOW}`,
                            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                background: `linear-gradient(135deg, #e88313, #e6ad00)`,
                                boxShadow: `0 6px 20px rgba(255, 144, 21, 0.35)`,
                                transform: 'translateX(2px)',
                            },
                            '&:active': {
                                transform: 'scale(0.97)',
                            },
                            '&.Mui-disabled': {
                                background: 'rgba(0, 0, 0, 0.08)',
                                color: 'rgba(0, 0, 0, 0.25)',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <Langs str="Next" />
                    </Button>
                </Box>
            </Box>
        );
    };

    /* ── Render ────────────────────────────────────────────────── */
    return (
        <Dialog
            fullScreen
            open={!!visible}
            onClose={handleClose}
            TransitionComponent={SlideTransition}
            sx={{
                '& .MuiDialog-paper': {
                    background: 'linear-gradient(160deg, #ffffff 0%, #fafbfc 50%, #f7f8fa 100%)',
                    overflow: 'hidden',
                },
            }}
            slotProps={{
                backdrop: {
                    sx: {
                        backgroundColor: 'rgba(0, 0, 0, 0.45)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)',
                    }
                }
            }}
        >
            {/* ── App Bar / Header ── */}
            <AppBar
                position="sticky"
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #fef9f5 100%)',
                    borderBottom: '1px solid rgba(255, 144, 21, 0.08)',
                    color: '#111318',
                }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, sm: 3 }, minHeight: '56px !important' }}>
                    {/* Tabs */}
                    <Tabs
                        value={activeTabIndex}
                        onChange={(e, val) => setActiveTabIndex(val)}
                        sx={{
                            minHeight: 48,
                            '& .MuiTabs-indicator': {
                                backgroundColor: ORANGE,
                                height: 3,
                                borderRadius: '3px 3px 0 0',
                            },
                            '& .MuiTab-root': {
                                minHeight: 48,
                                textTransform: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                                color: '#6b7280',
                                letterSpacing: '-0.01em',
                                gap: 0.75,
                                transition: 'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
                                '&.Mui-selected': {
                                    color: ORANGE,
                                    fontWeight: 600,
                                },
                                '&:hover': {
                                    color: '#374151',
                                    backgroundColor: 'rgba(255, 144, 21, 0.04)',
                                },
                            },
                        }}
                    >
                        <Tab
                            icon={<BoltIcon sx={{ fontSize: 18 }} />}
                            iconPosition="end"
                            label={<Langs str="general" />}
                        />
                        <Tab
                            icon={<EditIcon sx={{ fontSize: 16 }} />}
                            iconPosition="end"
                            label={<Langs str="entry" />}
                        />
                    </Tabs>

                    {/* Close button */}
                    <IconButton
                        onClick={handleClose}
                        sx={{
                            color: '#9ca3af',
                            width: 40,
                            height: 40,
                            borderRadius: '12px',
                            transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                                color: '#ef4444',
                                backgroundColor: 'rgba(239, 68, 68, 0.08)',
                                transform: 'rotate(90deg)',
                            },
                            '&:active': {
                                transform: 'scale(0.90)',
                            },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                </Toolbar>
            </AppBar>

            {/* ── Body ── */}
            <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {body()}
            </Box>
        </Dialog>
    );
};

export default CollabProjectForm;
